import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Customer, Staff } from "@/types";

/**
 * The `auth` slice, reshaped to the original's own (module **88745**).
 *
 * Its initial state there is
 * `{ fetchingCustomerInfo: false, customer: {}, isLoggedIn: false, staff: null, isStaff: false }`
 * and the five fields are not interchangeable:
 *
 * - **`isLoggedIn`** — not `customer != null`. The original keeps a customer
 *   record around after a failed lookup (it seeds one from the phone number
 *   alone) and its screens still have to treat that visitor as signed out, so
 *   the flag is separate and is what every gate reads.
 * - **`staff` / `isStaff`** — `isStaff` is *derived*: `setStaffState` stores
 *   `null` when the payload is absent **or `is_disabled`**, then sets
 *   `isStaff = staff != null`. A disabled staff member is therefore not staff,
 *   which is the whole point of the check.
 * - **`fetchingCustomerInfo`** — raised by every customer/staff lookup and by
 *   the staff switch; the account chip shows a spinner off it.
 *
 * **One deliberate deviation.** The original's signed-out `customer` is `{}`,
 * because its record is built up field by field from Firebase. Ours is `null`:
 * `/web/auth/*` answers with a whole customer or with nothing, and a `{}` here
 * would make `customer.phone` read as "signed in with no number". `isLoggedIn`
 * carries the meaning either way, which is why it was added rather than
 * inferred.
 *
 * The thunks (`setAuthLogIn`, `setLogout`, `switchStaffAuth`, …) live in
 * `store/thunks/auth.ts`, because they dispatch into the cart, history and
 * offers slices and importing those here would close a cycle.
 */

/**
 * The staff record lives in `@/types` now, because `lib/api.ts` returns it and
 * a slice is the wrong place for a wire shape.
 */
export type { Staff };

/**
 * `staff.uid`, with this platform's shape accounted for.
 *
 * The original reads `auth.staff.uid` — a Firebase uid — in five places. The
 * stored `tenant_staffs` document here has no `uid` field at all; its own
 * `_id` is that identity, and `:4001`'s `GET /tenant-staff/:tenantId/:uid`
 * resolves either. One helper so no call site has to remember.
 */
export const staffUid = (staff?: Staff | null): string =>
  String(staff?.uid ?? staff?._id ?? "");

export interface AuthState {
  fetchingCustomerInfo: boolean;
  customer: Customer | null;
  isLoggedIn: boolean;
  staff: Staff | null;
  isStaff: boolean;
}

const initialState: AuthState = {
  fetchingCustomerInfo: false,
  customer: null,
  isLoggedIn: false,
  staff: null,
  isStaff: false,
};

const authSlice = createSlice({
  name: "auth-slice",
  initialState,
  reducers: {
    /** `I` — replace the customer record, leaving `isStaff` alone. */
    setCurrentCustomer(state, action: PayloadAction<Customer | null>) {
      state.customer = action.payload ?? null;
    },
    /** `N` — the login write: customer and `isLoggedIn` together. */
    setAuthState(
      state,
      action: PayloadAction<{ customer?: Customer | null; isLoggedIn?: boolean }>,
    ) {
      if ("customer" in action.payload) {
        state.customer = action.payload.customer ?? null;
      }
      if (action.payload.isLoggedIn != null) {
        state.isLoggedIn = Boolean(action.payload.isLoggedIn);
      }
    },
    /**
     * `P` — `staff = payload == null || payload.is_disabled ? null : payload`,
     * then `isStaff = staff != null`. Reproduced exactly: a disabled staff
     * member signs in as nobody, not as staff.
     */
    setStaffState(state, action: PayloadAction<Staff | null>) {
      const next =
        action.payload == null || action.payload.is_disabled
          ? null
          : action.payload;
      state.staff = next;
      state.isStaff = next != null;
    },
    setFetchingCustomerInfo(state, action: PayloadAction<boolean>) {
      state.fetchingCustomerInfo = Boolean(action.payload);
    },
    /** `w` — back to the initial state, whole. */
    resetAuthState() {
      return initialState;
    },
  },
});

export const {
  setCurrentCustomer,
  setAuthState,
  setStaffState,
  setFetchingCustomerInfo,
  resetAuthState,
} = authSlice.actions;
export default authSlice.reducer;
